import React from 'react';
import { FormSection } from 'redux-form';
import classNames from 'classnames';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import { TextAreaField } from 'src/jbc-front/components/Form';

import { concatParamsToUrl } from 'src/utils/Http';
import { ErrorsSection } from 'src/components';

import { EMPLOYEE_SHOW } from 'src/constants/EndpointUrls';
import * as CssClassNames from 'src/constants/CssClassNames';
import MemoList from 'src/features/Memos';

import PaymentsSectionMockup from './PaymentsSectionMockup';
import AttendancesSectionMockup from './AttendancesSectionMockup';
import DeductionsSectionMockup from './DeductionsSectionMockup';
import DiligenceSectionMockup from './DiligenceSectionMockup';

import AllPays from './AllPays';
import styles from './EmployeePayForm.scss';
import EmployeePayButtonGroups from './EmployeePayButtonGroups';

const greenTextList = ['Taxable', 'Non taxable'];

const EmployeePayForm = ({
  submit,
  handleSubmit,
  errors,
  employeeId,
  employeePayId,
  initialValues,
  changeViewModeToTrue,
  changeViewModeToFalse,
  isViewOnly,
  paymentMonthConfirmed,
  allowanceItems,
  allEmployeePays,
  confirmPayJobs,
  downloadPayCsvJobs,
  importPayCsvJobs,
  clearRollbackError,
  motivationStreaks
}) => {
  const { t } = useTranslation();

  const attendanceMergeValue = (clientAllowanceItems, values) => {
    const regexp = /customizeAllowances\[(\d+)\]/;
    const allAllowanceItems = clientAllowanceItems;
    const allowanceValues = values;
    for (let i = 0; i < allAllowanceItems.length; i += 1) {
      const index = allAllowanceItems[i].name.match(regexp);
      Object.assign(allAllowanceItems[i], { value: allowanceValues[index[1]] });
    }
  };

  attendanceMergeValue(initialValues.allowanceItems.all, initialValues.customizeAllowances);

  const { employeePay, calcErrors } = initialValues;
  const hasRunningPayJobs =
    !_.isEmpty(confirmPayJobs.inProgress) ||
    !_.isEmpty(confirmPayJobs.waiting) ||
    !_.isEmpty(downloadPayCsvJobs.inProgress) ||
    !_.isEmpty(downloadPayCsvJobs.waiting) ||
    !_.isEmpty(importPayCsvJobs.inProgress) ||
    !_.isEmpty(importPayCsvJobs.waiting);

  const disabledEditBtn = paymentMonthConfirmed || hasRunningPayJobs;

  return (
    <form className={classNames(CssClassNames.FORM, CssClassNames.CLIENT_FORM)} onSubmit={handleSubmit}>
      <ErrorsSection errors={errors} />
      <ErrorsSection errors={calcErrors} title={t('features.EmployeePays.components.EmployeePayForm.Error_occurred_during_calculation')} />

      <div className={styles.name}>
        <div className={styles.memo}>
          <MemoList employeeId={employeeId} size={44} />
        </div>
        <div className={styles.nameFull}>
          <a href={concatParamsToUrl(EMPLOYEE_SHOW, { id: employeeId })}>{employeePay.fullName}</a>
        </div>
      </div>

      <AllPays allEmployeePays={allEmployeePays} employeePay={employeePay} />
      <EmployeePayButtonGroups
        isViewOnly={isViewOnly}
        submit={submit}
        disabledEditBtn={disabledEditBtn}
        hasRunningPayJobs={hasRunningPayJobs}
        changeViewModeToFalse={changeViewModeToFalse}
        changeViewModeToTrue={changeViewModeToTrue}
        paymentMonthConfirmed={paymentMonthConfirmed}
        clearRollbackError={clearRollbackError}
        employeePayId={employeePayId}
      />
      <div>
        <div className={styles.payment}>
          {/* 支給 Earning */}
          <div className={styles.paymentNew}>
            <table className={`${styles.tablePayment} m-table-obvious`}>
              <thead>
                <tr>
                  <th colSpan="2">{t('features.EmployeePays.components.EmployeePayForm.earning')}</th>
                </tr>
              </thead>
              <PaymentsSectionMockup greenTextList={greenTextList} fields={initialValues.payments} isViewOnly={isViewOnly} name="payments" />
            </table>
          </div>
          {/* 控除 Deduction */}
          <div className={styles.paymentNew}>
            <table className={`${styles.tablePayment} m-table-obvious`}>
              <thead>
                <tr>
                  <th colSpan="2">{t('features.EmployeePays.components.EmployeePayForm.deduction')}</th>
                </tr>
              </thead>
              <DeductionsSectionMockup greenTextList={greenTextList} fields={initialValues.deductions} isViewOnly={isViewOnly} name="deductions" />
            </table>
          </div>
          {/* 集計 Payment */}
          <div className={styles.paymentNew}>
            <table className={`${styles.tablePayment} m-table-obvious`}>
              <thead>
                <tr>
                  <th colSpan="2">{t('features.EmployeePays.components.EmployeePayForm.payment')}</th>
                </tr>
              </thead>
              <PaymentsSectionMockup greenTextList={greenTextList} fields={initialValues.summaries} isViewOnly={isViewOnly} name="summaries" noLine noEdit />
            </table>
            <table className={`${styles.tablePayment} m-table-obvious`} style={{ marginTop: '1rem' }}>
              <thead>
                <tr>
                  <th colSpan="2">{t('features.EmployeePays.components.EmployeePayForm.base_for_tax_and_insurance')}</th>
                </tr>
              </thead>
              <PaymentsSectionMockup
                greenTextList={greenTextList}
                fields={initialValues.baseTaxAndSsc}
                isViewOnly={isViewOnly}
                name="base-salary"
                noLine
                noEdit
              />
            </table>
          </div>
        </div>
        {motivationStreaks.length > 0 && (
          <div className={styles.attendance}>
            <div className={styles.paymentNew}>
              <table className={`${styles.tablePayment} m-table-obvious`}>
                <thead>
                  <tr>
                    <th colSpan={2}>{t('features.EmployeePays.components.EmployeePayForm.diligence_allowance')}</th>
                  </tr>
                </thead>
                <DiligenceSectionMockup fields={motivationStreaks} isViewOnly={isViewOnly} />
              </table>
            </div>
          </div>
        )}

        <div className={styles.attendance}>
          <div className={styles.paymentNew}>
            <table className={`${styles.tablePayment} m-table-obvious`}>
              <thead>
                <tr>
                  <th colSpan="2">{t('features.EmployeePays.components.EmployeePayForm.attendance')}</th>
                </tr>
              </thead>
              <AttendancesSectionMockup fields={allowanceItems.all} isViewOnly={isViewOnly} />
            </table>
          </div>
        </div>
        <div className={styles.attendance}>
          <div className={styles.paymentNew}>
            <table className={`${styles.tablePayment} m-table-obvious`}>
              <thead>
                <tr>
                  <th>{t('features.EmployeePays.components.EmployeePayForm.remark')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div className="m-table-obvious-remarks">
                      <FormSection name="employeePay">
                        <TextAreaField name="memo" disabled={isViewOnly} />
                      </FormSection>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </form>
  );
};

export default EmployeePayForm;
